import React from 'react';
import { Grid, List, Minus, Plus } from 'lucide-react';
import CustomSelect from '../CustomSelect';

export type ViewMode = 'grid' | 'list';
export type GroupBy = 'none' | 'type' | 'video';

interface MarkedTabControlsProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  tilesPerRow: number;
  onTilesPerRowChange: (count: number) => void;
  showDetails: boolean;
  onShowDetailsChange: (show: boolean) => void;
  groupBy: GroupBy;
  onGroupByChange: (group: GroupBy) => void;
}

const MarkedTabControls: React.FC<MarkedTabControlsProps> = ({
  viewMode,
  onViewModeChange,
  tilesPerRow,
  onTilesPerRowChange,
  showDetails,
  onShowDetailsChange,
  groupBy,
  onGroupByChange,
}) => {
  const minTiles = 2;
  const maxTiles = 8;

  return (
    <div className="marked-controls">
      {/* View Mode Toggle */}
      <div className="marked-control-group">
        <button
          type="button"
          className={`marked-control-btn ${viewMode === 'grid' ? 'active' : ''}`}
          onClick={() => onViewModeChange('grid')}
          title="Grid view"
          aria-label="Grid view"
        >
          <Grid size={16} />
        </button>
        <button
          type="button"
          className={`marked-control-btn ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => onViewModeChange('list')}
          title="List view"
          aria-label="List view"
        >
          <List size={16} />
        </button>
      </div>

      {/* Zoom Controls - only visible in grid mode */}
      {viewMode === 'grid' && (
        <div className="marked-control-group zoom-controls">
          <button
            type="button"
            className="marked-control-btn"
            onClick={() => onTilesPerRowChange(Math.min(tilesPerRow + 1, maxTiles))}
            disabled={tilesPerRow >= maxTiles}
            title="Smaller tiles (more per row)"
            aria-label="Decrease tile size"
          >
            <Minus size={16} />
          </button>
          <span className="zoom-label">{tilesPerRow}</span>
          <button
            type="button"
            className="marked-control-btn"
            onClick={() => onTilesPerRowChange(Math.max(tilesPerRow - 1, minTiles))}
            disabled={tilesPerRow <= minTiles}
            title="Larger tiles (fewer per row)"
            aria-label="Increase tile size"
          >
            <Plus size={16} />
          </button>
        </div>
      )}

      {/* Show Details Toggle - only visible in grid mode */}
      {viewMode === 'grid' && (
        <label className="marked-control-checkbox">
          <input
            type="checkbox"
            checked={showDetails}
            onChange={(e) => onShowDetailsChange(e.target.checked)}
          />
          <span>Details</span>
        </label>
      )}

      {/* Group By Dropdown */}
      <div className="marked-control-select">
        <span>Group</span>
        <CustomSelect
          value={groupBy}
          onChange={(value) => onGroupByChange(value as GroupBy)}
          options={[
            { value: 'none', label: 'None' },
            { value: 'type', label: 'By Type' },
            { value: 'video', label: 'By Video' },
          ]}
        />
      </div>
    </div>
  );
};

export default MarkedTabControls;
